import React, {useState} from 'react';
import axios from 'axios';
import classNames from 'classnames';

interface Config {
  targetType?: string;
  apiBaseUrl?: string; // e.g. /api/findability/
}

interface Props extends Config {
}

const Findability: React.FC<Props> = ({targetType = 'other', apiBaseUrl = '/api/findability/'}) => {

  const [status, setStatus] = useState<'initial' | 'votedYes' | 'votedNo' | 'submitted'>('initial');
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const targetUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleVote = async (found: boolean) => {
    try {
      await axios.post(`${apiBaseUrl}vote/`, {
        target_url: targetUrl,
        target_type: targetType,
        found,
      });
      if (found) {
        setStatus('votedYes');
      } else {
        setStatus('votedNo');
      }
    } catch (err) {
      console.error('Failed to submit vote', err);
      // Don't block the user on network failure.
      if (found) setStatus('votedYes');
      else setStatus('votedNo');
    }
  };

  const handleSubmitFeedback = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${apiBaseUrl}feedback/`, {
        target_url: targetUrl,
        target_type: targetType,
        found: false,
        comment,
      });
      setStatus('submitted');
    } catch (err) {
      console.error('Failed to submit feedback', err);
      setError(window.gettext('Failed to submit feedback. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (status === 'votedYes' || status === 'submitted') {
    return (
      <div className="p-4 bg-brand-olive-50 rounded-lg text-center max-w-2xl mx-auto" role="alert">
        <p className="text-brand-olive-900 font-medium">{window.gettext('Thanks for your feedback!')}</p>
      </div>
    );
  }

  return (
    <div className="border-t border-brand-sky-200 mt-4 pt-4 max-w-2xl mx-auto">
      {status === 'initial' && (
        <div className="flex items-center gap-4">
          <span className="font-secondary font-medium text-base text-secondary">
            {window.gettext('Did you find what you were looking for?')}
          </span>

          <div className="flex gap-2">
            {/* Thumbs Up */}
            <button
              type="button"
              onClick={() => handleVote(true)}
              className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors group"
              aria-label={window.gettext('Yes')}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-secondary group-hover:text-blue-500"
              >
                <path
                  d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {/* Thumbs Down */}
            <button
              type="button"
              onClick={() => handleVote(false)}
              className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors group"
              aria-label={window.gettext('No')}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-secondary group-hover:text-red-600"
              >
                <path
                  d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {status === 'votedNo' && (
        <div className="max-w-2xl mx-auto">
          <h3 className="text-lg font-medium text-brand-sky-950 mb-4">{window.gettext('Thanks for your feedback!')}</h3>
          <p className="text-sm text-brand-sky-700 mb-4">
            {window.gettext('Help us improve by telling us what was missing:')}
          </p>
          <form onSubmit={handleSubmitFeedback}>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              className="w-full shadow-sm focus:ring-brand-sky-500 focus:border-brand-sky-500 block sm:text-sm border-brand-sky-300 rounded-md p-2"
              rows={3}
              placeholder={window.gettext('Optional comment...')}
            />
            {error && <p className="mt-2 text-sm text-base-error-500">{error}</p>}
            <div className="mt-4 flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className={classNames(
                  'inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-brand-sky-800 hover:bg-brand-sky-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-sky-500',
                  {'opacity-50 cursor-not-allowed': isSubmitting}
                )}
              >
                {isSubmitting ? window.gettext('Sending...') : window.gettext('Send Feedback')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Findability;
